import { ROLES, type UserRole } from '@shared/config/constants'
import { ROUTES } from '@shared/config/routes'
import type { Notification, NotificationCategory, NotificationChange } from '../model/types'
import { NotifIconType } from '../ui/constants'

export const CATEGORY_LABEL_KEY: Record<NotificationCategory, string> = {
  Requests: 'notification.tab_requests',
  Users: 'notification.users',
  Payments: 'notification.payments',
  Chats: 'notification.tab_chats',
  Olympiads: 'notification.olympiads',
  Tasks: 'notification.tasks',
  Results: 'notification.results',
  Certificates: 'notification.certificates',
}

/** Role-specific label overrides. Admin/Rep keep the default `Users` → "Users"/"Участники",
 *  but participant legacy renders this bucket as "Профиль" / "Profile". */
const ROLE_CATEGORY_LABEL_OVERRIDES: Partial<
  Record<UserRole, Partial<Record<NotificationCategory, string>>>
> = {
  [ROLES.PARTICIPANT]: {
    Users: 'notification.profile',
  },
}

export function getCategoryLabelKey(
  cat: NotificationCategory,
  role: UserRole | null | undefined
): string {
  const override = role ? ROLE_CATEGORY_LABEL_OVERRIDES[role]?.[cat] : undefined
  return override ?? CATEGORY_LABEL_KEY[cat]
}

export const TAB_ALL = 'all'

const TAB_CATEGORIES_BY_ROLE: Record<UserRole, NotificationCategory[]> = {
  [ROLES.ADMIN]: ['Requests', 'Users', 'Payments'],
  // Portal order: Participants / Results / Chat → `Users` renders as "Участники" via rep namespace.
  [ROLES.REPRESENTATIVE]: ['Users', 'Results', 'Chats', 'Certificates'],
  [ROLES.PARTICIPANT]: ['Olympiads', 'Tasks', 'Users', 'Payments'],
}

export function getTabCategories(role: UserRole | null | undefined): NotificationCategory[] {
  return role ? TAB_CATEGORIES_BY_ROLE[role] : TAB_CATEGORIES_BY_ROLE[ROLES.ADMIN]
}

/** @deprecated Use getTabCategories(role). Kept for NotificationsPanel until it migrates. */
export const TAB_CATEGORIES: NotificationCategory[] = TAB_CATEGORIES_BY_ROLE[ROLES.ADMIN]

export function categoryToIconType(cat: NotificationCategory): NotifIconType {
  const map: Record<NotificationCategory, NotifIconType> = {
    Requests: 'users',
    Users: 'user',
    Chats: 'message',
    Payments: 'card',
    Olympiads: 'olympiad',
    Tasks: 'task',
    Results: 'result',
    Certificates: 'certificate',
  }
  return map[cat]
}

const CATEGORY_ROUTE_BY_ROLE: Record<UserRole, Partial<Record<NotificationCategory, string>>> = {
  [ROLES.ADMIN]: {},
  [ROLES.REPRESENTATIVE]: {
    Chats: ROUTES.REPRESENTATIVE_CHAT,
    Users: ROUTES.REPRESENTATIVE_MEMBERS,
    Results: ROUTES.REPRESENTATIVE_RESULTS,
  },
  [ROLES.PARTICIPANT]: {
    Olympiads: ROUTES.PARTICIPANT_OLYMPIADS,
    Tasks: ROUTES.PARTICIPANT_TASKS,
    Users: ROUTES.PARTICIPANT_MY_WAY,
    Payments: ROUTES.PARTICIPANT_PAYMENTS,
  },
}

export function categoryToRoute(
  cat: NotificationCategory,
  role: UserRole | null | undefined
): string | null {
  if (!role) return null
  return CATEGORY_ROUTE_BY_ROLE[role][cat] ?? null
}

export function firstLine(message: string | null | undefined): string {
  if (!message) return ''
  return message.split('\n')[0] ?? message
}

export interface ParsedChange {
  label: string
  old: string
  new: string
  hasOld: boolean
}

/** Profile-change requests arrive with the diff in one of three shapes depending on the
 *  backend version: top-level `changes`, `request.difference`, or a `payload` blob (sometimes
 *  serialized as a string). Match the legacy admin renderer's resolution order. */
function resolveChangesSource(notification: Notification): unknown {
  if (notification.changes) return notification.changes
  if (notification.request?.difference) return notification.request.difference

  let payload: unknown = notification.payload
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload)
    } catch {
      return null
    }
  }
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if (p.changes) return p.changes
    const looksLikeDiff = Object.values(p).some(
      (v) =>
        v !== null &&
        typeof v === 'object' &&
        ('old' in (v as object) ||
          'new' in (v as object) ||
          'old_value' in (v as object) ||
          'new_value' in (v as object))
    )
    if (looksLikeDiff) return p
  }
  return null
}

/**
 * Поля, которые представитель видит в списке изменений: неконтактные поля профиля и
 * олимпиады (те, у которых есть подписи в `useNotificationsData`). Это список разрешённого,
 * а не запрещённого: профиль сохраняется и вложенными `parent{}`/`teacher{}`, и под
 * другими именами контактов — любое поле вне списка скрыто.
 */
const REPRESENTATIVE_VISIBLE_FIELDS: ReadonlySet<string> = new Set([
  'city',
  'grade',
  'school',
  'country',
  'full_name_en',
  'full_name_ru',
  'olympiad',
  'amount',
])

/** `hideField` для представителя: скрыто всё, чего нет в списке разрешённого. */
export function isHiddenFromRepresentative(field: string): boolean {
  return !REPRESENTATIVE_VISIBLE_FIELDS.has(field)
}

/** Поля-ссылки, у которых без `*_display` голое число ничего не говорит админу. */
const ID_REFERENCE_FIELDS: ReadonlySet<string> = new Set(['representative_id'])

function displayValue(field: string, raw: unknown, hasDisplay: boolean): string {
  if (raw == null) return ''
  if (!hasDisplay && ID_REFERENCE_FIELDS.has(field) && raw !== '') return `#${String(raw)}`
  return String(raw)
}

export function parseChanges(
  notification: Notification,
  fieldLabels: Record<string, string>,
  hideField?: (field: string) => boolean
): ParsedChange[] {
  const changes = resolveChangesSource(notification)
  if (!changes) return []
  const visible = <T>(entries: T[], fieldOf: (e: T) => string): T[] =>
    hideField ? entries.filter((e) => !hideField(fieldOf(e))) : entries

  const labelFor = (field: string) =>
    fieldLabels[field] ?? (field.charAt(0).toUpperCase() + field.slice(1)).replace(/_/g, ' ')

  if (Array.isArray(changes)) {
    return visible(changes as NotificationChange[], (c) => c.field).map((c) => {
      // *_display carries the human-readable value for FK fields (city etc.);
      // old/new hold raw ids there, so display always wins.
      const oldRaw = c.old_display ?? c.old ?? c.old_value
      const newRaw = c.new_display ?? c.new ?? c.new_value
      return {
        label: labelFor(c.field),
        old: displayValue(c.field, oldRaw, c.old_display != null),
        new: displayValue(c.field, newRaw, c.new_display != null),
        hasOld: oldRaw !== undefined && oldRaw !== null && oldRaw !== '',
      }
    })
  }

  if (typeof changes === 'object') {
    return visible(Object.entries(changes as Record<string, unknown>), ([f]) => f).map(
      ([field, raw]) => {
        let oldRaw: unknown
        let newRaw: unknown
        let oldHasDisplay = false
        let newHasDisplay = false
        if (raw && typeof raw === 'object') {
          const v = raw as {
            old?: unknown
            old_value?: unknown
            old_display?: unknown
            new?: unknown
            new_value?: unknown
            new_display?: unknown
          }
          if (
            'old' in v ||
            'old_value' in v ||
            'new' in v ||
            'new_value' in v ||
            'old_display' in v ||
            'new_display' in v
          ) {
            oldRaw = v.old_display ?? v.old ?? v.old_value
            newRaw = v.new_display ?? v.new ?? v.new_value
            oldHasDisplay = v.old_display != null
            newHasDisplay = v.new_display != null
          } else {
            newRaw = raw
          }
        } else {
          newRaw = raw
        }
        return {
          label: labelFor(field),
          old: displayValue(field, oldRaw, oldHasDisplay),
          new: displayValue(field, newRaw, newHasDisplay),
          hasOld: oldRaw !== undefined && oldRaw !== null && oldRaw !== '',
        }
      }
    )
  }

  return []
}
